import useAsync from 'react-use/lib/useAsync';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef, EntityRefLink } from '@backstage/plugin-catalog-react';
import {
  InfoCard,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';

/**
 * Lists catalog entities owned by any group the signed-in user belongs
 * to.
 *
 * Ownership comes from the token's ownershipEntityRefs, which are
 * derived from Entra group membership at sign-in. A user who owns
 * nothing is a normal state, not an error — say so plainly rather than
 * rendering an empty card that reads as broken.
 */
export const MyOwnedEntitiesCard = () => {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);

  const { value, loading, error } = useAsync(async () => {
    const identity = await identityApi.getBackstageIdentity();
    const groupRefs = identity.ownershipEntityRefs.filter(ref =>
      ref.startsWith('group:'),
    );

    if (groupRefs.length === 0) {
      return [];
    }

    const response = await catalogApi.getEntities({
      filter: groupRefs.map(owner => ({ 'relations.ownedBy': owner })),
      fields: ['kind', 'metadata.name', 'metadata.namespace', 'spec.type'],
    });

    return response.items;
  }, [catalogApi, identityApi]);

  if (loading) {
    return (
      <InfoCard title="What you own">
        <Progress />
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="What you own">
        <ResponseErrorPanel error={error} />
      </InfoCard>
    );
  }

  const entities = value ?? [];

  return (
    <InfoCard title="What you own">
      {entities.length === 0 ? (
        <Typography>
          You don't own anything yet. Once your team is onboarded as a
          tenant, its services and resources appear here.
        </Typography>
      ) : (
        <List dense>
          {entities.map(entity => (
            <ListItem
              key={`${entity.kind}-${entity.metadata.namespace}-${entity.metadata.name}`}
            >
              <ListItemText
                primary={<EntityRefLink entityRef={entity} />}
                secondary={`${entity.kind}${
                  entity.spec?.type ? ` · ${entity.spec.type}` : ''
                }`}
              />
            </ListItem>
          ))}
        </List>
      )}
    </InfoCard>
  );
};
